import { css } from '@emotion/react'
import { Tooltip, colors, Divider } from 'plume-ui'
import { useState, useRef } from 'react'
import { useUser } from '../../../../contexts/AuthContext'
import { deleteCompanyStatus, registerCompanyStatus } from '@/lib/api/company'
import {
  STATUS_SETTING_ON_CAMPANY_PAGE,
  SUM_OF_STATUS_SETTING,
  trackEvent,
} from '@/lib/tracking'
import { statusSettingPath } from '@/lib/url'
import { Status } from '../../../../types/api'
import { IcomoonIcon } from '../../../atoms/IcomoonIcon'
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside'

type Props = {
  shortname: string
  statuses: Status[]
  selectedStatus: Status | null
  setSelectedStatus: React.Dispatch<React.SetStateAction<Status | null>>
}

export const StatusIcon: React.FC<Props> = ({
  shortname,
  statuses,
  selectedStatus,
  setSelectedStatus,
}) => {
  const currentUser = useUser()

  const [statusListOpening, setStatusListOpening] = useState(false)

  const statusSettingLink = () => {
    window.open(statusSettingPath, '_blank')
  }

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside([ref], () => setStatusListOpening(false))

  const handleChangeCompanyStatus = async (status: Status) => {
    await registerCompanyStatus(shortname, status.id)
    setSelectedStatus(status)
    setStatusListOpening(false)
    trackEvent(currentUser, STATUS_SETTING_ON_CAMPANY_PAGE)
    trackEvent(currentUser, SUM_OF_STATUS_SETTING)
  }

  const handleDeleteCompanyStatus = async () => {
    await deleteCompanyStatus(shortname)
    setSelectedStatus(null)
    setStatusListOpening(false)
    trackEvent(currentUser, STATUS_SETTING_ON_CAMPANY_PAGE)
    trackEvent(currentUser, SUM_OF_STATUS_SETTING)
  }

  return (
    <div
      className="status"
      css={css`
        min-width: 64px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
      `}
      onClick={() => setStatusListOpening(!statusListOpening)}
      ref={ref}
    >
      <Tooltip
        className="status-icon-tooltip"
        label={selectedStatus ? selectedStatus.name : 'ステータスを選択'}
        place="top"
      >
        <div
          className="status-color"
          css={css`
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            position: relative;
            border: ${selectedStatus
              ? `1px solid ${selectedStatus.color}`
              : `1px dashed ${colors.white00}`};
            margin: auto;
            background-color: ${selectedStatus
              ? selectedStatus.color
              : 'transparent'};
            &:hover {
              cursor: pointer;
              &:before {
                background-color: rgba(0, 0, 0, 0.08);
                position: absolute;
                border-radius: 50%;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                content: ' ';
              }
            }
          `}
        >
          {selectedStatus ? (
            <div
              className="status-symbol"
              css={css`
                z-index: 1;
              `}
            >
              {selectedStatus.symbol}
            </div>
          ) : (
            <IcomoonIcon
              name="check1"
              css={css`
                font-size: 20px;
                z-index: 1;
              `}
            />
          )}
        </div>
        <div
          css={css`
            margin-top: 4px;
            font-size: 11px;
            line-height: 1.25;
            text-align: center;
          `}
        >
          ステータス
        </div>
      </Tooltip>
      {statusListOpening && (
        <div
          css={css`
            position: relative;
          `}
          className="statuses"
        >
          <ul
            css={css`
              width: 216px;
              max-height: 345px;
              margin-top: 7.5px;
              background: ${colors.white00};
              border-radius: 2px;
              box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
              padding: 5px 0;
              position: absolute;
              list-style: none;
              white-space: nowrap;
              transition: 0.2s;
              z-index: 50;
              overflow: auto;
              li:hover {
                background-color: ${colors.gray40};
              }
            `}
          >
            <li
              onClick={async () => handleDeleteCompanyStatus()}
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
              `}
            >
              {selectedStatus === null && (
                <IcomoonIcon
                  name={'check1'}
                  css={css`
                    position: absolute;
                    font-size: 12px;
                    left: 12px;
                    z-index: 1;
                    color: ${colors.black30};
                  `}
                />
              )}
              <div
                css={css`
                  font-size: 14px;
                  color: ${colors.black30};
                  padding-left: 8px;
                `}
              >
                （空白）
              </div>
            </li>
            {statuses.map((status) => (
              <li
                key={status.id}
                onClick={async () => handleChangeCompanyStatus(status)}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  line-height: 125%;
                  letter-spacing: 0.02em;
                  gap: 8px;
                  font-size: 14px;
                  color: ${colors.black30};
                  cursor: pointer;
                  padding: 7px 16px 7px 36px;
                `}
              >
                {status.id === selectedStatus?.id && (
                  <IcomoonIcon
                    name={'check1'}
                    css={css`
                      position: absolute;
                      font-size: 12px;
                      left: 12px;
                      z-index: 1;
                      color: ${colors.black30};
                    `}
                  />
                )}
                <div>
                  <div
                    css={css`
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      background-color: ${status.color};
                    `}
                  ></div>
                </div>
                <div
                  css={css`
                    font-size: 14px;
                    color: ${colors.black30};
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                  className="status-name"
                >
                  {status.name}
                </div>
              </li>
            ))}
            <Divider
              css={css`
                margin: 4px 0;
              `}
            />
            <li
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
                position: relative;
              `}
              onClick={statusSettingLink}
            >
              <IcomoonIcon
                name={'pen-fill'}
                css={css`
                  position: absolute;
                  left: 12px;
                  z-index: 1;
                  color: ${colors.gray60};
                `}
              />
              ステータス項目を編集
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}
